import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Stack, Box, styled} from '@mui/material';
import {
  Body,
  Button,
  Headline,
  designSystemToken,
} from '@lightricks/react-design-system';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {
  ContentLabFlags,
  getAvailableFeatures,
} from '@contentlab/utils/featureUtils';
import onboardingWizardStore, {
  onboardingWizardActions,
} from '@contentlab/views/contentlab/pages/onboarding-page/onboardingWizardStore';
import translate from '@/utils/translate';
import {MinimalSelectionButton, ResponsiveStack} from '../../components';
import getFeatureInfo from './getFeatureInfo';

const MainContainer = styled(Stack)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100vh',
}));

const HeaderSection = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  borderRadius: '30px 30px 0 0',
  padding: '24px 32px 24px 32px',
  width: '100%',
  maxWidth: '504px',
}));

const HeaderContent = styled(Stack)(() => ({
  alignItems: 'center',
  gap: '8px',
}));

const Title = styled(Headline)(() => ({
  textAlign: 'center',
  marginBottom: '0',
}));

const Subtitle = styled(Body)(() => ({
  textAlign: 'center',
  marginBottom: '0',
}));

const FeaturesGrid = styled(Box)(() => ({
  padding: '20px 0',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '20px',
  overflowY: 'auto',
  paddingInlineEnd: '12px',
  ...scrollbarMixins.overrideScrollbarHover(false, 4),
}));

const FeatureItem = styled(Box)(() => ({
  flex: 1,
}));

const ButtonSection = styled(Box)(() => ({
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  padding: '16px 0 24px 0',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
}));

const ActionButton = styled(Button)(() => ({
  width: '100%',
}));

function FeatureSelectionMenu({onNext}: {onNext: () => void}) {
  const flags = useFlags();

  const activeFeatures = useMemo(
    () => getAvailableFeatures(flags as ContentLabFlags),
    [flags]
  );

  const {selectedFeature} = onboardingWizardStore(
    useShallow((state) => ({
      selectedFeature: state.selectedFeature,
    }))
  );

  return (
    <MainContainer>
      <ResponsiveStack
        justifyContent="center"
        alignItems="center"
        maxWidth="400px"
      >
        <HeaderSection>
          <HeaderContent>
            <Title size="lg">What do you want to do first?</Title>
            <Subtitle size="lg">
              Pick a tool to start — change anytime!
            </Subtitle>
          </HeaderContent>
        </HeaderSection>
        <FeaturesGrid>
          {activeFeatures.map((feature) => {
            const featureInfo = getFeatureInfo(feature);
            return (
              <FeatureItem key={feature}>
                <MinimalSelectionButton
                  item={featureInfo}
                  onClick={() => {
                    onboardingWizardActions.setSelectedFeature(feature);
                  }}
                  isSelected={selectedFeature === feature}
                  onMouseEnter={() => {
                    onboardingWizardActions.setHoveredFeature(feature);
                  }}
                  onMouseLeave={() => {
                    onboardingWizardActions.setHoveredFeature(null);
                  }}
                  isPopular={featureInfo.isPopular}
                />
              </FeatureItem>
            );
          })}
        </FeaturesGrid>
        <ButtonSection>
          <ActionButton
            appearance="neutral"
            mode="filled"
            size="large"
            onClick={onNext}
            disabled={selectedFeature === null}
          >
            {translate('Get Started')}
          </ActionButton>
        </ButtonSection>
      </ResponsiveStack>
    </MainContainer>
  );
}

export default FeatureSelectionMenu;
